import * as React from 'react';
import styled from 'styled-components';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { faFileDownload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TaskClient from 'httpClients/taskClient';
import { CenterModal, StyledButton, Typography } from 'components';
import { Epod } from 'models/task/epod';
import EpodDocument from 'components/pdf/epodDocument';
import { format } from 'date-fns';

interface SharePodReceiptModalProps {
  isShowBackButton: boolean;
  handleBackButtonModal?: () => void;
  handleCloseModal: () => void;
  taskId: string;
  isOps?: boolean;
}

export default function SharePodReceiptModal(props: SharePodReceiptModalProps) {
  const { handleCloseModal, isShowBackButton, taskId } = props;
  const [epod, setEpod] = React.useState<Epod | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  const generateEpod = async (id: string): Promise<Epod | null> => {
    const client = new TaskClient();
    let task = await client.getTask(id);
    if (props.isOps) {
      task = await client.sysAdminGetTask(id);
    }
    if (task && task.current_status === 'complete') {
      return await client.generateEpod(id);
    }
    return null;
  };

  const fetchTask = async (id: string): Promise<void> => {
    const client = new TaskClient();
    setIsLoading(true);
    try {
      const response = await client.getEpodByTaskId(id);
      setEpod(response);
    } catch (error) {
      // generate epod if epod not found but task status is complete
      const errorMessage = (error as Error).toString().toLowerCase();
      if (errorMessage === 'epod not found') {
        const epod = await generateEpod(id);
        if (epod) {
          setEpod(epod);
        } else {
          // todo: handle error
        }
      } else {
        // todo: handle error
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filename = (epodCreatedAt: Date, taskId: string) => {
    const id = taskId.split('-')[0];
    const dateEpodCreatedFormatted = format(new Date(epod.created_at), 'ddMMyy');
    return `EPOD-${dateEpodCreatedFormatted}-${id}`;
  };

  React.useEffect(() => {
    fetchTask(taskId);
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <CenterModal
      position={isMobile ? 'bottom' : 'top'}
      title={<PODModalTitle>Share Proof of Delivery Receipt</PODModalTitle>}
      width="small"
      isShowBackButton={isShowBackButton}
      closeButtonOnClick={() => handleCloseModal()}
      backButtonOnClick={() => {
        if (typeof props.handleBackButtonModal === 'function') {
          props.handleBackButtonModal();
        }
      }}
    >
      <Typography
        color="gray_600"
        as="p"
        customStyle={{
          marginBottom: '1.35rem',
        }}
      >
        View or download your proof of delivery receipt.
      </Typography>

      {epod ? (
        <PDFDownloadLink
          document={<EpodDocument data={epod} />}
          fileName={filename(epod.created_at, taskId)}
        >
          {() => (
            <>
              <StyledButton
                isLoading={isLoading}
                disabled={isLoading}
                style={{ marginRight: '1rem', minWidth: '126.42px' }}
                size={isMobile ? 'lg' : 'md'}
                buttonStyle="encourage"
                buttonType="neutral"
                fontWeight={600}
                icon={<FontAwesomeIcon icon={faFileDownload} />}
                isFullWidth={isMobile}
              >
                Download
              </StyledButton>
            </>
          )}
        </PDFDownloadLink>
      ) : (
        <StyledButton
          isLoading={true}
          disabled={true}
          style={{ marginRight: '1rem' }}
          size={isMobile ? 'lg' : 'md'}
          buttonStyle="encourage"
          buttonType="neutral"
          fontWeight={600}
          icon={<FontAwesomeIcon icon={faFileDownload} />}
          isFullWidth={isMobile}
        >
          Download
        </StyledButton>
      )}

      {!isMobile && (
        <StyledButton
          size="md"
          buttonStyle="encourage"
          buttonType="neutral"
          fontWeight={600}
          icon={<FontAwesomeIcon icon={faFileAlt} />}
          onClick={() => {
            window.open(`/tasks/${taskId}/epod`, '_blank');
          }}
        >
          View Receipt
        </StyledButton>
      )}
    </CenterModal>
  );
}

const PODModalTitle = styled.p`
  font-size: 1.7rem;
  margin-bottom: 1rem;
  margin-top: 0;
  font-weight: 600;
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.25rem;
    margin-top: 0.75rem;
  }
`;
